/**
 * OFFLINE SERVICE
 * 
 * Handles offline expense creation with background sync
 * 
 * Features:
 * - Queue expenses when offline
 * - Auto-sync when back online
 * - Optimistic UI updates
 * - Conflict resolution
 */

import { logger } from '../utils/logger';
import { CreateExpenseData } from '../types';

const DB_NAME = 'SettlerOfflineDB';
const DB_VERSION = 1;
const STORE_NAME = 'offlineExpenses';

interface OfflineExpense {
  id: string;
  data: CreateExpenseData;
  authToken: string;
  apiUrl: string;
  timestamp: number;
  retryCount: number;
}

class OfflineService {
  private db: IDBDatabase | null = null;

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger.debug('✅ IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          logger.debug('📦 Created offline expenses store');
        }
      };
    });
  }

  /**
   * Queue an expense for offline sync
   */
  async queueExpense(
    expenseData: CreateExpenseData,
    authToken: string,
    apiUrl: string
  ): Promise<string> {
    await this.init();

    const offlineExpense: OfflineExpense = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      data: expenseData,
      authToken,
      apiUrl,
      timestamp: Date.now(),
      retryCount: 0,
    };

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(offlineExpense);

      request.onerror = () => {
        logger.error('Failed to queue offline expense:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        logger.debug('✅ Queued offline expense:', offlineExpense.id);
        resolve(offlineExpense.id);
      };
    });
  }

  /**
   * Get all queued offline expenses
   */
  async getQueuedExpenses(): Promise<OfflineExpense[]> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => {
        logger.error('Failed to get queued expenses:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  /**
   * Get count of queued expenses
   */
  async getQueueCount(): Promise<number> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();

      request.onerror = () => {
        logger.error('Failed to get queue count:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  /**
   * Remove an expense from the queue
   */
  async removeFromQueue(id: string): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onerror = () => {
        logger.error('Failed to remove from queue:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        logger.debug('✅ Removed from queue:', id);
        resolve();
      };
    });
  }

  /**
   * Clear all queued expenses
   */
  async clearQueue(): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => {
        logger.error('Failed to clear queue:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        logger.debug('✅ Queue cleared');
        resolve();
      };
    });
  }

  /**
   * Register background sync (if supported)
   */
  async registerBackgroundSync(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
      logger.warn('Background Sync not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('sync-offline-expenses');
      logger.debug('✅ Background sync registered');
    } catch (error) {
      logger.error('Failed to register background sync:', error);
    }
  }

  /**
   * Check if device is online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Attempt to sync queued expenses manually
   */
  async syncNow(): Promise<{ success: number; failed: number }> {
    const expenses = await this.getQueuedExpenses();
    
    if (expenses.length === 0) {
      return { success: 0, failed: 0 };
    }

    logger.info(`🔄 Syncing ${expenses.length} offline expenses...`);

    let success = 0;
    let failed = 0;

    for (const expense of expenses) {
      try {
        const response = await fetch(`${expense.apiUrl}/api/expenses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${expense.authToken}`,
          },
          body: JSON.stringify(expense.data),
        });

        if (response.ok) {
          await this.removeFromQueue(expense.id);
          success++;
          logger.debug('✅ Synced expense:', expense.id);
        } else {
          failed++;
          logger.error('❌ Failed to sync expense:', expense.id, response.status);
        }
      } catch (error) {
        failed++;
        logger.error('❌ Failed to sync expense:', expense.id, error);
      }
    }

    logger.info(`✅ Sync complete: ${success} success, ${failed} failed`);
    return { success, failed };
  }
}

export const offlineService = new OfflineService();

